import type { TimeSlot } from "@/features/availability/types/availabilityTypes";

export const generateAvailabilityTimeSlots = (
  timeSlots: TimeSlot[],
  startAt: Date,
  endAt: Date,
  slotDurationMinutes: number,
  bufferBeforeMinutes = 0,
  bufferAfterMinutes = 0,
): { startAt: Date; endAt: Date }[] => {
  const slots: { startAt: Date; endAt: Date }[] = [];
  if (!Number.isFinite(slotDurationMinutes) || slotDurationMinutes <= 0 ||
    !Number.isFinite(bufferBeforeMinutes) || bufferBeforeMinutes < 0 ||
    !Number.isFinite(bufferAfterMinutes) || bufferAfterMinutes < 0 ||
    !Number.isFinite(startAt.getTime()) || !Number.isFinite(endAt.getTime()) || endAt <= startAt) return slots;
  const slotDurationMs = slotDurationMinutes * 60 * 1000;
  const incrementMs = 15 * 60 * 1000; // Always increment by 15 minutes
  const seen = new Set<number>();
  const windows: { start: number; end: number }[] = [];
  for (const window of timeSlots.map((slot) => ({ start: new Date(slot.start).getTime(), end: new Date(slot.end).getTime() }))
    .filter((slot) => Number.isFinite(slot.start) && Number.isFinite(slot.end) && slot.end > slot.start)
    .sort((a, b) => a.start - b.start)) {
    const previous = windows[windows.length - 1];
    if (previous && window.start <= previous.end) previous.end = Math.max(previous.end, window.end);
    else windows.push({ ...window });
  }

  for (const timeSlot of windows) {
    const slotStart = new Date(timeSlot.start);
    const slotEnd = new Date(timeSlot.end);
    const earliest = Math.max(slotStart.getTime() + bufferBeforeMinutes * 60_000, startAt.getTime());
    let currentStart = new Date(startAt.getTime() + Math.ceil((earliest - startAt.getTime()) / incrementMs) * incrementMs);

    while (currentStart < slotEnd && currentStart < endAt) {
      const currentEnd = new Date(currentStart.getTime() + slotDurationMs);
      if (currentEnd.getTime() + bufferAfterMinutes * 60_000 <= slotEnd.getTime() &&
        currentEnd <= endAt && !seen.has(currentStart.getTime())) {
        seen.add(currentStart.getTime());
        slots.push({
          startAt: new Date(currentStart),
          endAt: new Date(currentEnd),
        });
      }
      // Move to next 15-minute interval instead of full duration
      currentStart = new Date(currentStart.getTime() + incrementMs);
    }
  }

  return slots.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
};
